import { Analytics } from '@cobliteam/analytics';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import Typography from '@cobliteam/ui/components/Typography';
import { motion, type Variants } from 'motion/react';
import { nanoid } from 'nanoid';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { rememberHiddenMessage } from '../lib/hiddenPrompts';
import { buildPromptSuggestionAssistantPrompt, resolveAssistantPageContextLabel } from '../lib/pageContext';
import { useAIAssistant } from '../store';
import type { SendUserTextMessage } from '../types';
import AIIcon from './AIIcon';
import AIPromptSuggestions, { type AIPromptSuggestionItem } from './AIPromptSuggestions';

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const fadeInUp: Variants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

type AIAssistantWelcomeProps = {
  sendMessage: SendUserTextMessage;
};

const AIAssistantWelcome = ({ sendMessage }: AIAssistantWelcomeProps) => {
  const { t } = useTranslation('ai');
  const { currentLanguage } = useLanguage();
  const { dashboardDef } = useDashboard();
  const location = useLocation();
  const { createFakeThread, threadId, newThreadId, sidepanelOriginPath } = useAIAssistant();
  const originPath = sidepanelOriginPath || location.pathname;
  const isMonitoringOrigin = originPath.replace(/\/+$/, '') === '/monitoring';
  const originLabel = useMemo(() => resolveAssistantPageContextLabel(dashboardDef, originPath, currentLanguage), [currentLanguage, dashboardDef, originPath]);

  const reportSuggestionError = (err: unknown) => {
    Alert.error(t('send_failed'));
    const message = err instanceof Error ? err.message : String(err);
    // eslint-disable-next-line no-console
    console.error('[AIAssistantWelcome] sendMessage failed:', message, err);
  };

  const handleSuggestionClick = async (suggestion: AIPromptSuggestionItem) => {
    const promptWithContext = buildPromptSuggestionAssistantPrompt({
      page: originPath,
      pageLabel: originLabel,
      prompt: suggestion.prompt,
      tool: suggestion.tool,
    });

    Analytics.track('AI - Chat - Suggestion Clicked', {
      prompt: suggestion.prompt,
      label: suggestion.label,
      source: 'sidepanel',
      sourcePage: originLabel,
      sourcePath: originPath,
      tool: suggestion.tool,
    });

    try {
      if (!threadId) {
        await createFakeThread({ threadId: newThreadId });
      }

      Analytics.track('AI - Chat - Message Sent', {
        prompt: suggestion.prompt,
        promptLength: suggestion.prompt.length,
        hasMentions: false,
        source: 'sidepanel suggestion',
        sourcePage: originLabel,
        sourcePath: originPath,
        threadId: threadId || newThreadId,
        hiddenPrompt: true,
        tool: suggestion.tool,
      });
      const messageId = nanoid();
      rememberHiddenMessage({ id: messageId, text: promptWithContext });
      await sendMessage({ role: 'user', id: messageId, metadata: { hidden: true }, parts: [{ type: 'text', text: promptWithContext }] });
    } catch (err) {
      reportSuggestionError(err);
    }
  };

  return (
    <motion.div className='flex flex-1 flex-col items-center justify-center px-4 py-6' variants={staggerContainer} initial='initial' animate='animate'>
      <div className='flex w-fit shrink-0 flex-col items-center text-center'>
        {/* Icon */}
        <motion.div variants={fadeInUp} className='mb-4 flex items-center justify-center'>
          <AIIcon shape='circle' variant='solid' iconSize='xl' />
        </motion.div>

        {/* Title */}
        <motion.div variants={fadeInUp}>
          <Typography variant='heading-lg' className='font-semibold text-gray-12'>
            {t('welcome.title')}
          </Typography>
        </motion.div>

        {/* Subtitle */}
        <motion.div variants={fadeInUp} className='mt-1.5'>
          <Typography variant='text-md' className='text-gray-11'>
            {t('welcome.subtitle')}
          </Typography>
          {isMonitoringOrigin && originLabel && (
            <Typography variant='text-sm' className='mt-1.5 text-gray-10'>
              {t('welcome.context_prefix')}{' '}
              <Typography as='span' variant='text-sm-medium' className='text-gray-11'>
                {originLabel}
              </Typography>
            </Typography>
          )}
        </motion.div>

        {isMonitoringOrigin && (
          <motion.div variants={fadeInUp} className='mt-5 w-full max-w-[320px]'>
            <AIPromptSuggestions onSelect={(suggestion) => void handleSuggestionClick(suggestion)} />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

AIAssistantWelcome.displayName = 'AIAssistantWelcome';

export default AIAssistantWelcome;
